// Client-side error beacon (port of frontend-reactjs src/helpers/errorReporter.js).
//
// Catches window "error" + "unhandledrejection" events and POSTs a small JSON
// payload to the REST API's /api/clientlog endpoint. The API logs each event
// as a [clientjs] line in its pm2 error log, where the pm2err digest
// (noderestapi cli/pm2err-digest.js) groups them under the CLIENTJS tier by
// app/module/message — so mpages webview errors show up in the same triage
// loop as server errors.
//
// Design rules:
// - each error signature is sent ONCE per page load, hard cap 10 events/load
// - sendBeacon with text/plain (CORS "simple request" — no preflight); wrapped
//   in try + fetch keepalive fallback (old Chrome throws on Blob beacons,
//   crbug.com/490015)
// - cross-origin "Script error." and third-party analytics frames are dropped
// - never throws: telemetry must never break the app
//
// BUILD id: set REACT_APP_BUILD_ID in the build pipeline (e.g.
// REACT_APP_BUILD_ID=$(git rev-parse --short HEAD) npm run build) so digest
// entries distinguish current-build bugs from stale cached bundles.

import { DecryptED } from "../DecryptJS/Decrypt";

const ENDPOINT = (process.env.REACT_APP_BASE_URL || "") + "/api/clientlog";
const APP = "mpages";
const BUILD = process.env.REACT_APP_BUILD_ID || "unset";
const MAX_PER_LOAD = 10;

const sentSignatures = {};
let sentCount = 0;

function topFrame(stack) {
  if (!stack) return "";
  const lines = String(stack).split("\n");
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].indexOf("://") !== -1 && lines[i].indexOf("node_modules") === -1) {
      return lines[i].trim().slice(-200);
    }
  }
  return (lines[1] || "").trim().slice(-200);
}

// mpages routes are /<module>/:sid/:userid with the sid usually AES-encrypted
// as enc-... — best effort: second path segment, decrypted if needed
function getSid() {
  try {
    const seg = window.location.pathname.split("/")[2];
    if (!seg) return "";
    return String(DecryptED(seg)).slice(0, 12);
  } catch (e) {
    return "";
  }
}

export function reportClientError(message, extra) {
  try {
    if (!process.env.REACT_APP_BASE_URL) return;
    const msg = String(message || "unknown").slice(0, 400);
    const sig = msg.slice(0, 160);
    if (sentSignatures[sig] || sentCount >= MAX_PER_LOAD) return;
    sentSignatures[sig] = true;
    sentCount++;
    const payload = JSON.stringify({
      app: APP,
      host: window.location.hostname,
      build: BUILD,
      module: (window.location.pathname.split("/")[1] || "root").slice(0, 60),
      sid: getSid(),
      kind: (extra && extra.kind) || "error",
      msg: msg,
      frame: String((extra && extra.frame) || "").slice(0, 220),
      url: window.location.href.slice(0, 300),
    });
    let queued = false;
    try {
      if (navigator.sendBeacon) {
        queued = navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: "text/plain" }));
      }
    } catch (e) {
      queued = false;
    }
    if (!queued && window.fetch) {
      fetch(ENDPOINT, {
        method: "POST",
        body: payload,
        keepalive: true,
        headers: { "Content-Type": "text/plain" },
      }).catch(function () {});
    }
  } catch (e) {
    // never break the app over telemetry
  }
}

export function initErrorReporter() {
  window.addEventListener("error", function (e) {
    // cross-origin scripts surface as a bare "Script error." — no information
    if (!e.message || e.message === "Script error.") return;
    const frame =
      topFrame(e.error && e.error.stack) ||
      String(e.filename || "").slice(-160) + ":" + e.lineno + ":" + e.colno;
    // third-party analytics failing on ancient browsers is not our bug
    if (/cloudflareinsights\.com/.test(frame) || /cloudflareinsights\.com/.test(e.filename || "")) return;
    reportClientError(e.message, { frame: frame, kind: "error" });
  });
  window.addEventListener("unhandledrejection", function (e) {
    const r = e.reason;
    reportClientError((r && (r.message || String(r))) || "unhandled promise rejection", {
      frame: topFrame(r && r.stack),
      kind: "promise",
    });
  });
}
